import { AvatarDropdown, AvatarName, SelectLang } from '@/components';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import {
  keepPreviousData,
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { UserOutlined } from '@ant-design/icons';
import useStorePermission from './zustand/useStorePremission';
import { getPermissionOfCurrentUser } from './pages/System/Permission/services';
import { useEffect } from 'react';
import { hasPermission, routePermissionMap } from './utils/permission';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { data: dataPermission, isLoading } = useQuery({
    queryKey: ['permission-me'],
    queryFn: () => getPermissionOfCurrentUser(),
  });
  const { setPermissionCurrentUser, setPermissionLoading } = useStorePermission();

  useEffect(() => {
    setPermissionLoading(isLoading);
    if (dataPermission) {
      setPermissionCurrentUser(dataPermission);
    }
  }, [dataPermission, isLoading]);
  return <>{children}</>;
}

function menuDataRender(menuData: any[], permissionCurrentUser?: any) {
  const permissions = permissionCurrentUser?.permissions;
  const isOwner = permissionCurrentUser?.is_owner;
  if (isOwner) return menuData;

  const filterMenu = (menus: any[]): any[] =>
    menus
      .filter((menu) => {
        if (!menu.path) return false;
        const routeConfig = routePermissionMap[menu.path];
        if (!routeConfig) return false;
        if (!routeConfig.key) return false;
        if (!permissions?.[routeConfig.key]) return false;
        return hasPermission(permissions, routeConfig.key, routeConfig.action);
      })
      .map((menu) => ({
        ...menu,
      }));

  return filterMenu(menuData);
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  loading?: boolean;
  permissionCurrentUser?: any;
}> {
  console.log('one');

  const { location } = history;
  if (location.pathname !== loginPath) {
    return {
      permissionCurrentUser: await getPermissionOfCurrentUser(),
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      placeholderData: keepPreviousData,
    },
  },
});

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  console.log('Permisson', initialState?.permissionCurrentUser);
  console.log('two');
  return {
    // actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: <UserOutlined style={{ color: 'gray' }} />,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: undefined,
    footerRender: () => undefined,
    onPageChange: () => {
      const currentUser = localStorage.getItem('user_data');
      if (!currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      return (
        <QueryClientProvider client={queryClient}>
          <PermissionProvider>{children}</PermissionProvider>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState?.((preInitialState: any) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </QueryClientProvider>
      );
    },
    menuDataRender: (menuData) => menuDataRender(menuData, initialState?.permissionCurrentUser),
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: API_URL,
  timeout: 1000 * 60 * 5,
  headers: {
    'Content-Type': 'application/json',
  },
  ...errorConfig,
  requestInterceptors: [
    (config: any) => {
      const token = localStorage.getItem('access_token');

      if (token) {
        config.headers.Authorization = token;
      }
      return config;
    },
  ],
};
